/**
 * Tenant Layout (Native)
 *
 * Validates the tenantId in the URL before rendering child routes.
 * This file runs on native platforms (iOS/Android).
 *
 * Cases:
 * 1. tenantId === selectedTenant.id -> allow navigation
 * 2. tenantId in tenants -> switch tenant silently, allow navigation
 * 3. tenantId NOT in tenants -> redirect to Not Found
 */

import { ActivityIndicator } from "react-native";
import { Redirect, Stack, useLocalSearchParams } from "expo-router";
import { useUnistyles } from "react-native-unistyles";
import { View } from "@src/components";
import { useValidateTenantFromUrl, AccountNotConfiguredView } from "@src/features/tenants";

export default function TenantLayout() {
  const { theme } = useUnistyles();
  const params = useLocalSearchParams<{ tenantId: string }>();
  const urlTenantId = params.tenantId;

  const { isValidating, isValid, error, refreshTenants } = useValidateTenantFromUrl(urlTenantId);

  // Show error state
  if (error && !isValidating) {
    return (
      <AccountNotConfiguredView
        error={error?.message}
        onRetry={() => {
          void refreshTenants();
        }}
      />
    );
  }

  // Redirect to not-found when invalid (tenant not in user's available tenants)
  if (!isValidating && !isValid) {
    return <Redirect href="/not-found" />;
  }

  // Show loading state while validating
  if (isValidating) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" bg="background">
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // Render child routes
  return (
    <Stack
      screenOptions={{
        headerStyle: {
          backgroundColor: theme.colors.background,
        },
        headerTintColor: theme.colors.text,
        headerBackButtonDisplayMode: "minimal",
        contentStyle: {
          backgroundColor: theme.colors.background,
        },
      }}
    >
      <Stack.Screen name="sites/[siteId]/cameras/[cameraKey]/alarms" options={{ headerShown: false }} />
      <Stack.Screen name="sites/[siteId]/cameras/[cameraKey]/clips" options={{ headerShown: false }} />
    </Stack>
  );
}
